import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// Streaks racing past, recycled once they pass a near threshold - the
// classic "hyperspace" look. Extracted from what used to be the whole of
// visualizers/tunnel.js so it can also be swapped in as one of several
// rotating layers under NeonPulse's persistent rig/camera - see that
// file's layer-cycling comment. Standalone Tunnel points the camera
// straight down the flight axis for a "flying at you" effect; here the
// streaks still fly along world -Z but are viewed from NeonPulse's own
// angled 3rd-person camera, which reads as streaks crossing the scene
// rather than a first-person tunnel - a fitting variation, not a bug.
//
// Uses LineSegments2/LineMaterial (Three's official "fat lines" addon)
// instead of plain LineSegments/LineBasicMaterial. Plain WebGL line
// width is not reliably controllable - `LineBasicMaterial.linewidth` is
// silently ignored on nearly every platform (a long-standing WebGL/ANGLE
// limitation), so those streaks were always hairline-thin; it read as
// "smoothed" on desktop only because MSAA (antialias:true) softened the
// 1px edges. Once antialiasing was turned off for TV performance (see
// scene-manager.js), the same hairlines had nothing softening them and
// looked like harsh, ugly threads. LineSegments2 renders each segment as
// a real screen-space quad, so its width is genuinely controllable in
// CSS pixels and looks the same with or without MSAA.
// "Un tunnel de barres neons" - much fewer, much thicker/longer bars
// instead of a dense field of thin hairline streaks, was 500/2.4px/2.4
// units/12 units-per-sec.
const STREAK_COUNT = 45;
const TUNNEL_MIN_RADIUS = 1.5;
const TUNNEL_RADIUS = 6;
const Z_FAR = -45;
const Z_NEAR = 4; // recycle once a streak passes this point
const STREAK_LENGTH = 3.6;
const STREAK_WIDTH_PX = 9; // real CSS pixels, independent of render/AA settings - the outer, colored bar
// "Glossy": a second, thinner line drawn right on top of each bar,
// sharing the exact same endpoints, in a color mixed toward white - a
// bright hot core within the wider colored glow, the same two-tone look
// a real photographed neon tube has (a near-white-hot center with the
// tube's actual color only visible as the softer glow around it),
// rather than one flat-colored line.
const HIGHLIGHT_WIDTH_PX = 3.5;
const HIGHLIGHT_WHITEN_AMOUNT = 0.7;
const BASE_SPEED = 9; // was 12 - "travelling un peu moins rapide"
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x8f2fff, 0x2fff8f];
const WHITE = new THREE.Color(0xffffff);
// A shared beat-locked brighten (see beat-pulse.js) across every bar at
// once, via opacity - unlike disco-squares' rejected uniform flash (a
// dense, evenly-lit grid where that read as a mechanical strobe), a
// sparse field of already independently-colored/positioned bars reads
// as the whole tunnel catching the beat, not a structural artifact -
// "same to the beat reactions [as the rest of the scene]".
const BEAT_PULSE_AMOUNT = 0.4;
const BEAT_PULSE_DECAY = 9;
const BASE_OPACITY = 0.85;
const HIGHLIGHT_BASE_OPACITY = 0.9;


export class StreakLayer {
  init(scene) {
    this._z = new Float32Array(STREAK_COUNT);
    this._angle = new Float32Array(STREAK_COUNT);
    this._radius = new Float32Array(STREAK_COUNT);
    const positions = new Float32Array(STREAK_COUNT * 2 * 3); // 2 verts per streak
    const colors = new Float32Array(STREAK_COUNT * 2 * 3);
    const highlightColors = new Float32Array(STREAK_COUNT * 2 * 3);
    this._tmpColor = new THREE.Color();
    this._highlightColor = new THREE.Color();
    for (let i = 0; i < STREAK_COUNT; i++) {
      this._angle[i] = Math.random() * Math.PI * 2;
      this._radius[i] = TUNNEL_MIN_RADIUS + Math.random() * TUNNEL_RADIUS;
      this._z[i] = Z_FAR + Math.random() * (Z_NEAR - Z_FAR);
    }

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);
    geometry.setColors(colors);

    this._material = new LineMaterial({
      vertexColors: true,
      linewidth: STREAK_WIDTH_PX,
      transparent: true,
      opacity: BASE_OPACITY,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    // resolution must be the true CSS/display size, not the renderer's
    // (possibly downscaled - see RENDER_SCALE in scene-manager.js)
    // internal drawing-buffer size: the shader computes width as
    // linewidth/resolution.y in the render target's own NDC space, and
    // that fraction gets uniformly upscaled back to full size along with
    // everything else when the smaller canvas is stretched to fill the
    // screen - using the true display size here is what makes
    // STREAK_WIDTH_PX land as real CSS pixels on screen regardless of
    // whatever internal render scale is active.
    this._material.resolution.set(window.innerWidth, window.innerHeight);

    this._lines = new LineSegments2(geometry, this._material);
    scene.add(this._lines);

    // The InterleavedBuffer backing instanceStart/instanceEnd - mutating
    // its array directly and flagging needsUpdate is far cheaper per
    // frame than calling geometry.setPositions() again (which allocates
    // a brand new buffer every call).
    this._posBuffer = geometry.attributes.instanceStart.data;
    // Same InterleavedBuffer pattern for per-bar color - each bar keeps
    // its own independently-chosen artwork color (see _assignColor)
    // instead of the whole layer sharing one shared/cycling color, so
    // the tunnel actually reads as a mix of the cover's different colors
    // rather than a single hue at a time.
    this._colorBuffer = geometry.attributes.instanceColorStart.data;

    // "Glossy" bright core - a second, separate LineSegments2 built the
    // same way, sharing identical endpoints (written alongside the outer
    // bar's own every frame in update()) but thinner and whitened - see
    // HIGHLIGHT_WIDTH_PX/HIGHLIGHT_WHITEN_AMOUNT above.
    const highlightGeometry = new LineSegmentsGeometry();
    highlightGeometry.setPositions(positions);
    highlightGeometry.setColors(highlightColors);
    this._highlightMaterial = new LineMaterial({
      vertexColors: true,
      linewidth: HIGHLIGHT_WIDTH_PX,
      transparent: true,
      opacity: HIGHLIGHT_BASE_OPACITY,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    this._highlightMaterial.resolution.set(window.innerWidth, window.innerHeight);
    this._highlight = new LineSegments2(highlightGeometry, this._highlightMaterial);
    scene.add(this._highlight);
    this._highlightPosBuffer = highlightGeometry.attributes.instanceStart.data;
    this._highlightColorBuffer = highlightGeometry.attributes.instanceColorStart.data;

    this._time = 0;
    this._lastPaletteRef = null;
    // Each bar's own currently-assigned color, picked once at spawn/
    // recycle from whatever palette was active then - assigned for real
    // in the first update() call (which also handles "no palette yet",
    // via FALLBACK_COLORS) rather than duplicating the picking logic
    // here for an initial state that's about to be overwritten anyway.
    for (let i = 0; i < STREAK_COUNT; i++) {
      this._assignColor(i, FALLBACK_COLORS);
    }
  }

  // Picks a fresh random color from `colors` for bar i - both its outer
  // (colorBuffer) and glossy-highlight (whitened toward WHITE,
  // highlightColorBuffer) representation. Called once per bar at init,
  // again whenever that specific bar recycles (a new lap, a fresh pick),
  // and for every bar at once whenever the artwork palette itself
  // changes (so a track change doesn't wait for bars to individually
  // cycle back around before showing the new cover's colors).
  _assignColor(i, colors) {
    this._tmpColor.set(colors[Math.floor(Math.random() * colors.length)]);
    this._highlightColor.copy(this._tmpColor).lerp(WHITE, HIGHLIGHT_WHITEN_AMOUNT);
    const base = i * 6;
    const arr = this._colorBuffer.array;
    arr[base] = this._tmpColor.r;
    arr[base + 1] = this._tmpColor.g;
    arr[base + 2] = this._tmpColor.b;
    arr[base + 3] = this._tmpColor.r;
    arr[base + 4] = this._tmpColor.g;
    arr[base + 5] = this._tmpColor.b;
    const harr = this._highlightColorBuffer.array;
    harr[base] = this._highlightColor.r;
    harr[base + 1] = this._highlightColor.g;
    harr[base + 2] = this._highlightColor.b;
    harr[base + 3] = this._highlightColor.r;
    harr[base + 4] = this._highlightColor.g;
    harr[base + 5] = this._highlightColor.b;
  }

  update(deltaTime, profile) {
    this._material.resolution.set(window.innerWidth, window.innerHeight);
    this._highlightMaterial.resolution.set(window.innerWidth, window.innerHeight);

    // Fully frozen on pause (not just dim) - there's no "hold a look"
    // concept for streaks mid-flight the way there is for the rig.
    if (profile.playbackState !== 'PLAYING') return;

    this._time += deltaTime;
    const intensity = profile.director.intensity;
    const speed = BASE_SPEED * (0.5 + intensity);

    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.opacity = Math.min(1, BASE_OPACITY * pulse);
    this._highlightMaterial.opacity = Math.min(1, HIGHLIGHT_BASE_OPACITY * pulse);

    const dominant = profile.artworkPalette && profile.artworkPalette.dominantColors;
    const colors = dominant && dominant.length >= 2 ? dominant : FALLBACK_COLORS;
    // A track/palette change reassigns every bar at once, rather than
    // waiting for each one to individually recycle back around before
    // it shows the new cover's colors.
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      for (let i = 0; i < STREAK_COUNT; i++) this._assignColor(i, colors);
      this._colorBuffer.needsUpdate = true;
      this._highlightColorBuffer.needsUpdate = true;
    }

    const array = this._posBuffer.array;
    const highlightArray = this._highlightPosBuffer.array;
    for (let i = 0; i < STREAK_COUNT; i++) {
      this._z[i] += speed * deltaTime;
      if (this._z[i] > Z_NEAR) {
        this._z[i] = Z_FAR;
        this._angle[i] = Math.random() * Math.PI * 2;
        this._radius[i] = TUNNEL_MIN_RADIUS + Math.random() * TUNNEL_RADIUS;
        // A fresh color pick on every lap, not just on palette changes -
        // this is what actually keeps the mix of colors feeling alive
        // over a single long track instead of only ever varying once a
        // new song/cover comes in.
        this._assignColor(i, colors);
        this._colorBuffer.needsUpdate = true;
        this._highlightColorBuffer.needsUpdate = true;
      }
      const x = Math.cos(this._angle[i]) * this._radius[i];
      const y = Math.sin(this._angle[i]) * this._radius[i];
      const idx = i * 6;
      array[idx] = x;
      array[idx + 1] = y;
      array[idx + 2] = this._z[i];
      array[idx + 3] = x;
      array[idx + 4] = y;
      array[idx + 5] = this._z[i] - STREAK_LENGTH;
      // Identical endpoints - the highlight is drawn right on top of the
      // outer bar, not offset from it.
      highlightArray[idx] = x;
      highlightArray[idx + 1] = y;
      highlightArray[idx + 2] = this._z[i];
      highlightArray[idx + 3] = x;
      highlightArray[idx + 4] = y;
      highlightArray[idx + 5] = this._z[i] - STREAK_LENGTH;
    }
    this._posBuffer.needsUpdate = true;
    this._highlightPosBuffer.needsUpdate = true;
  }

  dispose(scene) {
    scene.remove(this._lines);
    this._lines.geometry.dispose();
    this._material.dispose();
    scene.remove(this._highlight);
    this._highlight.geometry.dispose();
    this._highlightMaterial.dispose();
  }
}
